import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Alert, IconButton, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { Add, Edit, Delete, Visibility, VisibilityOff } from '@mui/icons-material';
import * as bootstrap from 'bootstrap';
import PageHeader from '../components/PageHeader';
import apiService from '../services/api';

function LoginUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
    const [selectedUser, setSelectedUser] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        displayName: '',
        position: '',
        permission: 'admin'
    });
    const [error, setError] = useState('');

    // DataGrid columns
    const columns = [
        { field: '_id', headerName: 'ID', width: 200, hide: true },
        { field: 'username', headerName: 'Tài khoản', width: 200 },
        { 
            field: 'displayName', 
            headerName: 'Họ tên', 
            width: 250,
            valueGetter: (params) => params.row.displayName || params.row.fullName || ''
        },
        { 
            field: 'position', 
            headerName: 'Chức vụ', 
            width: 300
        },
        { 
            field: 'permission', 
            headerName: 'Quyền', 
            width: 150,
            valueGetter: (params) => {
                const permission = params.row.permission || params.row['permission '] || params.row.role;
                return permission === 'admin' ? 'Admin' : permission === 'managerment' ? 'Quản lý' : permission;
            }
        },
        {
            field: 'actions',
            headerName: 'Thao tác',
            width: 150,
            sortable: false,
            renderCell: (params) => (
                <Box>
                    <IconButton size="small" onClick={() => handleEdit(params.row)} title="Chỉnh sửa">
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(params.row)} title="Xóa">
                        <Delete fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    // Fetch users data
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await apiService.getLoginUsers();
            if (response.success) {
                setUsers(response.data);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Lỗi khi tải danh sách người dùng');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Handle create new user
    const handleCreate = () => {
        setDialogMode('create');
        setFormData({
            username: '',
            password: '',
            displayName: '',
            position: '',
            permission: 'admin'
        });
        setSelectedUser(null);
        setError('');
        setOpenDialog(true);
    };

    // Handle edit user
    const handleEdit = (user) => {
        setDialogMode('edit');
        setFormData({
            username: user.username || '',
            password: '', // Không hiển thị password cũ
            displayName: user.displayName || '',
            position: user.position || '',
            permission: user.permission || user['permission '] || 'admin'
        });
        setSelectedUser(user);
        setError('');
        setOpenDialog(true);
    };

    // Handle delete user
    const handleDelete = (user) => {
        if (window.confirm(`Bạn có chắc chắn muốn xóa tài khoản "${user.username}"?`)) {
            deleteUser(user._id);
        }
    };

    // Delete user API call
    const deleteUser = async (userId) => {
        try {
            const response = await apiService.deleteLoginUser(userId);
            if (response.success) {
                showSuccessToast('Xóa tài khoản thành công!');
                fetchUsers();
            } else {
                setError(response.error || 'Lỗi khi xóa tài khoản');
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            setError('Lỗi khi xóa tài khoản');
        }
    };

    // Handle form submit
    const handleSubmit = async () => {
        if (!formData.username || !formData.displayName) {
            setError('Vui lòng nhập đầy đủ thông tin bắt buộc');
            return;
        }

        if (dialogMode === 'create' && !formData.password) {
            setError('Vui lòng nhập mật khẩu');
            return;
        }

        try {
            let response;
            if (dialogMode === 'create') {
                response = await apiService.createLoginUser(formData);
            } else {
                // Nếu không nhập password mới, không cập nhật password
                const updateData = { ...formData };
                if (!updateData.password) {
                    delete updateData.password;
                }
                response = await apiService.updateLoginUser(selectedUser._id, updateData);
            }

            if (response.success) {
                showSuccessToast(dialogMode === 'create' ? 'Tạo tài khoản thành công!' : 'Cập nhật tài khoản thành công!');
                setOpenDialog(false);
                fetchUsers();
            } else {
                setError(response.error || 'Có lỗi xảy ra');
            }
        } catch (error) {
            console.error('Error saving user:', error);
            setError('Có lỗi xảy ra khi lưu thông tin');
        }
    };

    // Show success toast
    const showSuccessToast = (message) => {
        const toastElement = document.getElementById('successToast');
        const toastBody = toastElement.querySelector('.toast-body');
        toastBody.textContent = message;
        const toast = new bootstrap.Toast(toastElement);
        toast.show();
    };

    // Handle form input change
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <div className="container-fluid">
            <PageHeader 
                title="Quản lý tài khoản Admin" 
            />
            <div className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="fw-bold">Danh sách tài khoản</h3>
                    <Button 
                        variant="contained" 
                        startIcon={<Add />} 
                        onClick={handleCreate}
                        sx={{ backgroundColor: '#0098FA' }}
                    >
                        Thêm tài khoản
                    </Button>
                </div>

            {error && (
                <Alert severity="error" onClose={() => setError('')} className="mb-3">
                    {error}
                </Alert>
            )}

            <Box sx={{ height: 600, width: '100%' }}>
                <DataGrid
                    rows={users}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    loading={loading}
                    disableSelectionOnClick
                    getRowId={(row) => row._id}
                    sx={{
                        '& .MuiDataGrid-cell:hover': {
                            color: 'primary.main',
                        },
                    }}
                />
            </Box>

            {/* Dialog for Create/Edit */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {dialogMode === 'create' ? 'Thêm tài khoản mới' : 'Chỉnh sửa tài khoản'}
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" className="mb-3">
                            {error}
                        </Alert>
                    )}
                    
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Tài khoản *"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formData.username}
                        onChange={(e) => handleInputChange('username', e.target.value)}
                        disabled={dialogMode === 'edit'} // Không cho sửa username
                        className="mb-3"
                    />
                    
                    <TextField
                        margin="dense"
                        label={dialogMode === 'create' ? 'Mật khẩu *' : 'Mật khẩu mới (để trống nếu không đổi)'}
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        variant="outlined"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className="mb-3"
                        InputProps={{
                            endAdornment: (
                                <IconButton onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            ),
                        }}
                    />
                    
                    <TextField
                        margin="dense"
                        label="Họ tên *"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formData.displayName}
                        onChange={(e) => handleInputChange('displayName', e.target.value)}
                        className="mb-3"
                    />
                    
                    <TextField
                        margin="dense"
                        label="Chức vụ"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formData.position}
                        onChange={(e) => handleInputChange('position', e.target.value)}
                        className="mb-3"
                    />
                    
                    <FormControl fullWidth margin="dense" variant="outlined" className="mb-3">
                        <InputLabel>Quyền</InputLabel>
                        <Select
                            value={formData.permission}
                            onChange={(e) => handleInputChange('permission', e.target.value)}
                            label="Quyền"
                        >
                            <MenuItem value="admin">Admin</MenuItem>
                            <MenuItem value="managerment">Quản lý</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Hủy</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {dialogMode === 'create' ? 'Tạo' : 'Cập nhật'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Toast */}
            <div className="toast-container position-fixed top-0 end-0 p-3">
                <div id="successToast" className="toast" role="alert">
                    <div className="toast-header">
                        <strong className="me-auto text-success">Thành công</strong>
                        <button type="button" className="btn-close" data-bs-dismiss="toast"></button>
                    </div>
                    <div className="toast-body"></div>
                </div>
            </div>
            </div>
        </div>
    );
}

export default LoginUsers;